import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, DatePicker, Row, Select, Segmented, Spin, Statistic, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import { hrsToDuration } from '../utils/time';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Rolls over to hours past 60 minutes; see utils/time.
const hrsToTime = (hrs) => hrsToDuration(hrs) ?? '-';

function formatDay(d) {
  if (!d) return d;
  return dayjs(d).format('MMM D');
}

// Shared pivot cell renderer, same content as the monthly page
function PivotCell({ row }) {
  if (!row) return <span style={{ color: 'var(--ant-color-text-quaternary)' }}>-</span>;
  const lbl = t => <span style={{ color: 'var(--ant-color-text-secondary)' }}>{t}</span>;
  return (
    <div style={{ fontSize: 11, lineHeight: 1.7, textAlign: 'left' }}>
      <div>{lbl('Total:')}{' '}{row.TotalContactsHandled?.toLocaleString() ?? '-'}</div>
      <div>{lbl('ACW (All):')}{' '}{hrsToTime(row.AvgACWHrs)}</div>
      <div>{lbl('Add ACW (All):')}{' '}{hrsToTime(row.AvgAdditionalACWHrs)}</div>
      <div>{lbl('Talk (All):')}{' '}{hrsToTime(row.AvgAgentInteractionHoldHrs)}</div>
      <div>{lbl('AHT (All):')}{' '}{hrsToTime(row.AvgHandleHrs)}</div>
      <div>{lbl('In:')}{' '}{row.ContactsHandledIncoming?.toLocaleString() ?? '-'}</div>
      <div>{lbl('Out:')}{' '}{row.ContactsHandledOutbound?.toLocaleString() ?? '-'}</div>
      <div>{lbl('CB:')}{' '}{row.CallbackContactsHandled?.toLocaleString() ?? '-'}</div>
    </div>
  );
}

/**
 * CNE daily stats, standalone or embedded in the Campaign Summary.
 *
 * Embedded it loses its heading, its campaign picker and its range picker and
 * takes the scope from the page above. The view toggle stays: which of the
 * five tables to read is the reader's choice either way.
 */
export default function CNEDailyStatsPage({
  embedded = false, campaignId = null, from: fromProp = null, to: toProp = null,
}) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [bounds, setBounds]   = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [viewMode, setViewMode] = useState('Agent Call Table');

  // Month to date by default. The monthly page loads every month at once,
  // which is fine at a row per agent per month; at a row per agent per day the
  // range has to be bounded, and the month in progress is the one being asked
  // about. On the 1st this is a single day, which is correct rather than empty.
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);

  // Strings, not dayjs objects: these are fetch dependencies, and a fresh
  // object every render would re-fetch forever.
  const from = embedded ? fromProp : range?.[0]?.format('YYYY-MM-DD');
  const to   = embedded ? toProp   : range?.[1]?.format('YYYY-MM-DD');

  // Only bounds the standalone range picker, which embedded mode does not show.
  useEffect(() => {
    if (embedded) return;
    api.get('/api/cne-daily-stats/dates')
      .then((r) => setBounds(r.data || null))
      .catch(() => setBounds(null));
  }, [embedded]);

  useEffect(() => {
    if (!from || !to) { setData([]); setLoading(false); return; }
    setLoading(true);
    api.get('/api/cne-daily-stats', {
      params: { date_from: from, date_to: to, ...(campaignId ? { campaign_id: campaignId } : {}) },
    })
      .then((r) => setData(r.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [from, to, campaignId]);

  const campaigns = useMemo(() => (
    [...new Set(data.map(r => r.CampaignName).filter(Boolean))].sort()
  ), [data]);

  const filteredData = useMemo(() => (
    selectedCampaign ? data.filter(r => r.CampaignName === selectedCampaign) : data
  ), [data, selectedCampaign]);

  const scorecard = useMemo(() => {
    if (!filteredData.length) return null;
    const inbound   = filteredData.reduce((s, r) => s + (r.ContactsHandledIncoming || 0), 0);
    const outbound  = filteredData.reduce((s, r) => s + (r.ContactsHandledOutbound  || 0), 0);
    const callbacks = filteredData.reduce((s, r) => s + (r.CallbackContactsHandled  || 0), 0);
    const acwRows   = filteredData.filter(r => r.AvgACWHrs != null);
    const addRows   = filteredData.filter(r => r.AvgAdditionalACWHrs != null);
    const talkRows  = filteredData.filter(r => r.AvgAgentInteractionHoldHrs != null);
    const htRows    = filteredData.filter(r => r.AvgHandleHrs != null);
    const avgACW    = acwRows.length  ? acwRows.reduce((s, r)  => s + Number(r.AvgACWHrs                  || 0), 0) / acwRows.length  : null;
    const avgAddACW = addRows.length  ? addRows.reduce((s, r)  => s + Number(r.AvgAdditionalACWHrs        || 0), 0) / addRows.length  : null;
    const avgTalk   = talkRows.length ? talkRows.reduce((s, r) => s + Number(r.AvgAgentInteractionHoldHrs || 0), 0) / talkRows.length : null;
    const avgHT     = htRows.length   ? htRows.reduce((s, r)   => s + Number(r.AvgHandleHrs               || 0), 0) / htRows.length   : null;
    return { inbound, outbound, callbacks, avgACW, avgAddACW, avgTalk, avgHT, days: new Set(filteredData.map(r => r.IntervalDate)).size };
  }, [filteredData]);

  // Agent Pivot — one column per day
  const { pivotDays, pivotRows } = useMemo(() => {
    const dSet = [...new Set(filteredData.map(r => r.IntervalDate))].sort();
    const agentMap = {};
    filteredData.forEach(r => {
      const key = r.AWSID || r.DisplayName;
      if (!agentMap[key]) agentMap[key] = { name: r.DisplayName, awsid: r.AWSID, days: {} };
      agentMap[key].days[r.IntervalDate] = r;
    });
    return {
      pivotDays: dSet,
      pivotRows: Object.values(agentMap).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    };
  }, [filteredData]);

  // Campaign Call Trends — agent rows aggregated per campaign per day
  const { campaignDays, campaignRows } = useMemo(() => {
    const dSet = [...new Set(filteredData.map(r => r.IntervalDate))].sort();
    const campMap = {};
    filteredData.forEach(r => {
      const camp = r.CampaignName || 'Unknown';
      if (!campMap[camp]) campMap[camp] = { name: camp, days: {} };
      const d = r.IntervalDate;
      if (!campMap[camp].days[d]) {
        campMap[camp].days[d] = {
          TotalContactsHandled: 0, ContactsHandledIncoming: 0,
          ContactsHandledOutbound: 0, CallbackContactsHandled: 0,
          _acw: [], _addAcw: [], _talk: [], _aht: [],
        };
      }
      const cell = campMap[camp].days[d];
      cell.TotalContactsHandled    += (r.TotalContactsHandled    || 0);
      cell.ContactsHandledIncoming += (r.ContactsHandledIncoming || 0);
      cell.ContactsHandledOutbound += (r.ContactsHandledOutbound || 0);
      cell.CallbackContactsHandled += (r.CallbackContactsHandled || 0);
      if (r.AvgACWHrs != null)                  cell._acw.push(Number(r.AvgACWHrs || 0));
      if (r.AvgAdditionalACWHrs != null)        cell._addAcw.push(Number(r.AvgAdditionalACWHrs || 0));
      if (r.AvgAgentInteractionHoldHrs != null) cell._talk.push(Number(r.AvgAgentInteractionHoldHrs || 0));
      if (r.AvgHandleHrs != null)               cell._aht.push(Number(r.AvgHandleHrs || 0));
    });
    Object.values(campMap).forEach(c => {
      Object.values(c.days).forEach(cell => {
        cell.AvgACWHrs                  = cell._acw.length    ? cell._acw.reduce((s, v) => s + v, 0)    / cell._acw.length    : null;
        cell.AvgAdditionalACWHrs        = cell._addAcw.length ? cell._addAcw.reduce((s, v) => s + v, 0) / cell._addAcw.length : null;
        cell.AvgAgentInteractionHoldHrs = cell._talk.length   ? cell._talk.reduce((s, v) => s + v, 0)   / cell._talk.length   : null;
        cell.AvgHandleHrs               = cell._aht.length    ? cell._aht.reduce((s, v) => s + v, 0)    / cell._aht.length    : null;
      });
    });
    return {
      campaignDays: dSet,
      campaignRows: Object.values(campMap).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [filteredData]);

  // Campaign Status Table -- the agent status rows summed per campaign per day.
  //
  // Occupancy is recomputed from the summed components rather than averaged.
  // The source figure is OnContact / (OnContact + Idle), which holds exactly on
  // every row checked, so a campaign's occupancy is that same ratio over the
  // campaign's totals. Averaging the agents' percentages would weigh a
  // two-hour shift the same as a ten-hour one.
  const campaignStatusRows = useMemo(() => {
    const SUM_COLS = [
      'TotalContactsHandled',
      'State2OnBreakHrs', 'State3LunchHrs', 'OnlineTimeHrs', 'OnContactTimeHrs',
      'IdleTimeHrs', 'NonProductiveTimeHrs', 'BusyTimeHrs', 'MissedCallTimeHrs',
      'State1ACWAdditionalHrs', 'State4ChatEmailHrs', 'State5SpecialProjectsHrs',
      'State6MeetingHrs', 'State8QACoachingHrs', 'State9OutboundHrs',
      'State10TrainingHrs', 'State11AwayFromDeskHrs', 'State12SystemIssueHrs',
      'State13DeclinedCallHrs', 'State14MissedCallHrs', 'State15FMLAAwayHrs',
    ];
    const map = {};
    filteredData.forEach((r) => {
      const key = `${r.IntervalDate}|${r.CampaignName || 'Unknown'}`;
      const acc = map[key] || (map[key] = {
        IntervalDate: r.IntervalDate,
        CampaignName: r.CampaignName || 'Unknown',
        Agents: new Set(),
      });
      if (r.AWSID) acc.Agents.add(r.AWSID);
      // Only a reported value counts. Folding a null in as a zero would make a
      // column the export never sent look like a campaign that never used it.
      SUM_COLS.forEach((c) => {
        if (r[c] != null) acc[c] = (acc[c] || 0) + Number(r[c]);
      });
    });
    return Object.values(map).map((a) => {
      const oc   = a.OnContactTimeHrs || 0;
      const idle = a.IdleTimeHrs || 0;
      return {
        ...a,
        AgentCount:   a.Agents.size,
        OccupancyPct: (oc + idle) > 0 ? (100 * oc) / (oc + idle) : null,
      };
    }).sort((x, y) => (y.IntervalDate || '').localeCompare(x.IntervalDate || '')
                   || x.CampaignName.localeCompare(y.CampaignName));
  }, [filteredData]);

  const makePivotDayCols = (days, firstCol) => [
    firstCol,
    ...days.map(d => ({
      title: formatDay(d),
      key: d,
      align: 'center',
      width: 155,
      render: (_, r) => <PivotCell row={r.days[d]} />,
    })),
  ];

  const agentPivotColumns = makePivotDayCols(pivotDays, {
    title: 'Agent', key: 'agent', fixed: 'left', width: 180,
    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    render: (_, r) => r.name,
  });
  const campaignPivotColumns = makePivotDayCols(campaignDays, {
    title: 'Campaign', key: 'campaign', fixed: 'left', width: 180,
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (_, r) => r.name,
  });

  const tableColumns = [
    { title: 'Date', dataIndex: 'IntervalDate', key: 'date', width: 110,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.IntervalDate || '').localeCompare(b.IntervalDate || '') },
    { title: 'Agent',    dataIndex: 'DisplayName',  key: 'agent',    sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'campaign', sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
    { title: 'Contacts Handled', dataIndex: 'TotalContactsHandled', key: 'total', align: 'right', sorter: (a, b) => a.TotalContactsHandled - b.TotalContactsHandled, render: v => v?.toLocaleString() ?? '-' },
    { title: 'Avg ACW (All)',       key: 'avgacw',    align: 'right', sorter: (a, b) => (a.AvgACWHrs || 0) - (b.AvgACWHrs || 0), render: (_, r) => hrsToTime(r.AvgACWHrs) },
    { title: 'Avg Add. ACW (All)',  key: 'avgaddacw', align: 'right', sorter: (a, b) => (a.AvgAdditionalACWHrs || 0) - (b.AvgAdditionalACWHrs || 0), render: (_, r) => hrsToTime(r.AvgAdditionalACWHrs) },
    { title: 'Avg Talk Time (All)', key: 'avgtalk',   align: 'right', sorter: (a, b) => (a.AvgAgentInteractionHoldHrs || 0) - (b.AvgAgentInteractionHoldHrs || 0), render: (_, r) => hrsToTime(r.AvgAgentInteractionHoldHrs) },
    { title: 'Avg AHT (All)',       key: 'avght',     align: 'right', sorter: (a, b) => (a.AvgHandleHrs || 0) - (b.AvgHandleHrs || 0), render: (_, r) => hrsToTime(r.AvgHandleHrs) },
    { title: 'Inbound',   dataIndex: 'ContactsHandledIncoming', key: 'inbound',   align: 'right', sorter: (a, b) => a.ContactsHandledIncoming - b.ContactsHandledIncoming, render: v => v?.toLocaleString() ?? '-' },
    { title: 'Outbound',  dataIndex: 'ContactsHandledOutbound', key: 'outbound',  align: 'right', sorter: (a, b) => a.ContactsHandledOutbound - b.ContactsHandledOutbound, render: v => v?.toLocaleString() ?? '-' },
    { title: 'Callbacks', dataIndex: 'CallbackContactsHandled', key: 'callbacks', align: 'right', sorter: (a, b) => a.CallbackContactsHandled - b.CallbackContactsHandled, render: v => v?.toLocaleString() ?? '-' },
  ];

  // Where the day went, rather than what was handled. Same three identifying
  // columns as the call table so the two read as views of one row, then the
  // agent states. Every duration is a fraction of an hour, as everywhere else
  // in this export.
  const hrsCol = (title, key, width = 130) => ({
    title, key, width, align: 'right',
    sorter: (a, b) => (a[key] || 0) - (b[key] || 0),
    render: (_, r) => hrsToTime(r[key]),
  });

  const statusColumns = [
    { title: 'Date', dataIndex: 'IntervalDate', key: 'IntervalDate', width: 110, fixed: 'left',
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.IntervalDate || '').localeCompare(b.IntervalDate || '') },
    { title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 170, fixed: 'left', ellipsis: true,
      sorter: (a, b) => (a.DisplayName || '').localeCompare(b.DisplayName || '') },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 140, ellipsis: true,
      sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
    { title: 'Contacts Handled', dataIndex: 'TotalContactsHandled', key: 'TotalContactsHandled', width: 140, align: 'right',
      sorter: (a, b) => a.TotalContactsHandled - b.TotalContactsHandled,
      render: (v) => v?.toLocaleString() ?? '-' },
    hrsCol('On Break',      'State2OnBreakHrs',        110),
    hrsCol('Lunch',         'State3LunchHrs',          100),
    hrsCol('Online',        'OnlineTimeHrs',           110),
    hrsCol('On Contact',    'OnContactTimeHrs',        120),
    hrsCol('Idle',          'IdleTimeHrs',             100),
    hrsCol('Non-Productive','NonProductiveTimeHrs',    140),
    { title: 'Occupancy', dataIndex: 'OccupancyPct', key: 'OccupancyPct', width: 110, align: 'right',
      sorter: (a, b) => (a.OccupancyPct || 0) - (b.OccupancyPct || 0),
      render: (v) => v != null ? `${Number(v).toFixed(2)}%` : '-' },
    hrsCol('Busy',            'BusyTimeHrs',              100),
    hrsCol('Missed Call Time','MissedCallTimeHrs',        140),
    hrsCol('Chat or Email',   'State4ChatEmailHrs',       130),
    hrsCol('Special Projects','State5SpecialProjectsHrs', 145),
    hrsCol('Meeting',         'State6MeetingHrs',         110),
    hrsCol('QA Coaching',     'State8QACoachingHrs',      125),
    hrsCol('Outbound',        'State9OutboundHrs',        115),
    hrsCol('Training',        'State10TrainingHrs',       110),
    hrsCol('Away from Desk',  'State11AwayFromDeskHrs',   145),
    hrsCol('System Issue',    'State12SystemIssueHrs',    125),
    hrsCol('Declined Call',   'State13DeclinedCallHrs',   130),
    hrsCol('Missed Call',     'State14MissedCallHrs',     125),
    hrsCol('FMLA Away',       'State15FMLAAwayHrs',       120),
  ];

  // The same columns, minus the agent. Agents replaces it: a campaign total is
  // only readable next to how many people it is spread across.
  const campaignStatusColumns = [
    ...statusColumns.filter((c) => c.key !== 'DisplayName'),
  ];
  campaignStatusColumns.splice(2, 0, {
    title: 'Agents', dataIndex: 'AgentCount', key: 'AgentCount', width: 90, align: 'right',
    sorter: (a, b) => a.AgentCount - b.AgentCount,
    render: (v) => v?.toLocaleString() ?? '-',
  });

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && (
        <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>Campaign CNE Daily Stats</Title>
      )}

      <Row gutter={12} align="middle" style={{ marginBottom: 16 }}>
        {/* The campaign and the window come from the page above when embedded,
            so its own pickers would be two sets of controls disagreeing. */}
        {!embedded && (
          <>
            <Col>
              <Select value={selectedCampaign || ''} onChange={v => setSelectedCampaign(v)} style={{ width: 200 }} placeholder="All Campaigns">
                <Option value="">All Campaigns</Option>
                {campaigns.map(c => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </Col>
            <Col>
              <RangePicker
                value={range}
                onChange={setRange}
                allowClear={false}
                disabledDate={(d) => {
                  if (!bounds?.min_date || !bounds?.max_date) return false;
                  return d.isBefore(dayjs(bounds.min_date), 'day') || d.isAfter(dayjs(bounds.max_date), 'day');
                }}
              />
            </Col>
          </>
        )}
        <Col>
          <Segmented options={['Agent Call Table', 'Agent Status Table', 'Campaign Status Table', 'Agent Pivot', 'Campaign Call Trends']} value={viewMode} onChange={setViewMode} />
        </Col>
      </Row>

      {bounds && bounds.row_count === 0 && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="No daily stats have been uploaded yet."
          description="Upload the Historical Metrics Report from Admin > CNE Daily Stats Upload."
        />
      )}

      {scorecard && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { title: 'Inbound Calls',       value: scorecard.inbound },
            { title: 'Outbound Calls',      value: scorecard.outbound },
            { title: 'Callbacks',           value: scorecard.callbacks },
            { title: 'Days',                value: scorecard.days },
            { title: 'Avg ACW (All)',       value: hrsToTime(scorecard.avgACW) },
            { title: 'Avg Add. ACW (All)',  value: hrsToTime(scorecard.avgAddACW) },
            { title: 'Avg Talk Time (All)', value: hrsToTime(scorecard.avgTalk) },
            { title: 'Avg AHT (All)',       value: hrsToTime(scorecard.avgHT) },
          ].map((s) => (
            <Card key={s.title} size="small" variant="outlined" style={{ flex: '1 1 0', minWidth: 100 }}>
              <Statistic title={s.title} value={s.value} />
            </Card>
          ))}
        </div>
      )}

      <Spin spinning={loading}>
        {viewMode === 'Agent Call Table' && (
          <Table size="small" dataSource={filteredData} columns={tableColumns}
            rowKey={(r, i) => `${r.IntervalDate}-${r.AWSID || ''}-${i}`}
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} rows` }}
            locale={{ emptyText: 'No daily stats in this date range.' }} />
        )}
        {viewMode === 'Agent Status Table' && (
          <Table size="small" dataSource={filteredData} columns={statusColumns}
            rowKey={(r, i) => `${r.IntervalDate}-${r.AWSID || ''}-${i}`}
            scroll={{ x: 'max-content' }}
            sticky
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} rows` }}
          />
        )}

        {viewMode === 'Campaign Status Table' && (
          <Table size="small" dataSource={campaignStatusRows} columns={campaignStatusColumns}
            rowKey={(r) => `${r.IntervalDate}-${r.CampaignName}`}
            scroll={{ x: 'max-content' }}
            sticky
            pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} rows` }}
            locale={{ emptyText: 'No daily stats in this date range.' }} />
        )}

        {viewMode === 'Agent Pivot' && (
          <Table size="small" dataSource={pivotRows} columns={agentPivotColumns}
            rowKey={r => r.awsid || r.name} pagination={false} scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No daily stats in this date range.' }} />
        )}
        {viewMode === 'Campaign Call Trends' && (
          <Table size="small" dataSource={campaignRows} columns={campaignPivotColumns}
            rowKey={r => r.name} pagination={false} scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No daily stats in this date range.' }} />
        )}
      </Spin>
    </div>
  );
}
